export type Lang = 'en' | 'zh';

export const t = {
  en: {
    // App
    appName: 'PQC Navigator',
    companyName: 'DataBrushing AI',
    companyFull: 'DataBrushing AI · AI | Security | IP | Consultancy',
    heroTitle: 'Post-Quantum Cryptography Readiness Assessment',
    heroSubtitle: 'Assess your cryptographic infrastructure against NIST FIPS 203/204, CNSA 2.0, and ETSI quantum-safe migration standards',
    heroCaption: 'TLS 1.3 · SSH · STARTTLS · DNSSEC · IPsec — Multi-protocol PQC compliance analysis',

    // Auth
    login: 'Login',
    register: 'Register',
    email: 'Email / Account',
    password: 'Password',
    displayName: 'Display Name (optional)',
    passwordHint: 'Password (min. 6 characters)',
    loginBtn: 'Login',
    registerBtn: 'Create Account',
    welcomeBack: 'Welcome Back',
    loginSubtitle: 'Sign in to your account to continue',
    registerTitle: 'Create Account',
    registerSubtitle: 'Fill in the details below to register',
    verifyEmailSent: 'Account created! Please check your email to verify.',
    emailNotVerified: 'Please verify your email before logging in',

    // Nav
    adminPanel: 'Admin Panel',
    logout: 'Logout',
    quotaTooltip: 'Daily scans remaining',
    roleAdmin: 'Admin',
    roleAdvanced: 'Advanced',
    roleBasic: 'Basic',

    // Scan
    scanResults: 'Scan Results',
    targets: 'targets',
    exportPDF: 'Export PDF',
    generating: 'Generating...',
    scanInProgress: 'Scan in progress',
    scanStarting: 'Starting scan...',
    targetsCompleted: 'targets completed',
    scanning: 'Scanning',

    // Result card
    analysisReport: 'Analysis Report',
    port: 'Port',
    tlsVersion: 'TLS Version',
    risk: 'Risk',
    kex: 'Key Exchange (KEX)',
    pki: 'Digital Signature (PKI)',
    cipherSuite: 'Cipher Suite',
    certificate: 'PKI Certificate',
    subjectDN: 'Subject DN',
    issuerDN: 'Issuer DN',
    validity: 'Validity',
    publicKey: 'Public Key',
    algorithms: 'Algorithms',
    downloadPEM: 'Download PEM Certificate',
    recommendation: 'Recommendation',

    // Risk
    highRisk: '⚠ High Risk',
    mediumRisk: '⚡ Medium Risk',
    lowRisk: '✓ Low Risk',

    // Risk advice
    adviceHigh: 'Both Key Exchange (KEX) and Digital Signature (PKI) do not support PQC algorithms. This site faces high risk from quantum computer attacks. Plan migration to post-quantum cryptography standards as soon as possible.',
    adviceMediumKex: 'Key Exchange KEM supports PQC, but Digital Signature PKI has not been upgraded. Recommend upgrading the signature algorithm as well.',
    adviceMediumPki: 'Digital Signature PKI supports PQC, but Key Exchange KEX has not been upgraded. Recommend upgrading the key exchange mechanism.',
    adviceLow: 'Both Key Exchange and Digital Signature support PQC algorithms. This site has good quantum security.',

    // Footer
    footerLeft: '© DataBrushing AI',
    footerRight: 'AI | Security | IP | Consultancy · https://databrushing.ai',

    // Login page features
    feature1: 'TLS 1.3 / SSH / STARTTLS PQC Analysis',
    feature2: 'ML-KEM · ML-DSA · SLH-DSA Detection',
    feature3: 'X.509 Certificate & DNSSEC Audit',
    feature4: 'NIST SP 800-208 / CNSA 2.0 Reports',
    platformSubtitle: 'Enterprise-Grade Quantum-Safe Readiness Platform',
  },
  zh: {
    appName: 'PQC Navigator',
    companyName: 'DataBrushing AI',
    companyFull: '解智能資料梳理股份有限公司 · AI | Security | IP | Consultancy',
    heroTitle: '後量子密碼學就緒度評估',
    heroSubtitle: '依據 NIST FIPS 203/204、CNSA 2.0、ETSI 量子安全遷移標準，評估您的密碼基礎設施',
    heroCaption: 'TLS 1.3 · SSH · STARTTLS · DNSSEC · IPsec — 多協定 PQC 合規分析',

    login: '登入',
    register: '註冊',
    email: 'Email / 帳號',
    password: '密碼',
    displayName: '顯示名稱（選填）',
    passwordHint: '密碼（至少 6 碼）',
    loginBtn: '登入',
    registerBtn: '建立帳號',
    welcomeBack: '歡迎回來',
    loginSubtitle: '請登入您的帳號繼續使用',
    registerTitle: '建立帳號',
    registerSubtitle: '填寫以下資訊完成註冊',
    verifyEmailSent: '帳號已建立，請查收驗證信！',
    emailNotVerified: '請先驗證您的電子郵件後再登入',

    adminPanel: '管理後台',
    logout: '登出',
    quotaTooltip: '今日剩餘掃描次數',
    roleAdmin: '管理員',
    roleAdvanced: '進階',
    roleBasic: '基本',

    scanResults: '掃描結果',
    targets: '個目標',
    exportPDF: '匯出 PDF',
    generating: '產生中...',
    scanInProgress: '掃描進行中',
    scanStarting: '啟動掃描中...',
    targetsCompleted: '個目標完成',
    scanning: '正在掃描',

    analysisReport: '分析報告',
    port: 'Port',
    tlsVersion: 'TLS 版本',
    risk: '風險',
    kex: '金鑰交換 (KEX)',
    pki: '數位簽章 (PKI)',
    cipherSuite: 'Cipher Suite',
    certificate: 'PKI 憑證',
    subjectDN: 'Subject DN',
    issuerDN: 'Issuer DN',
    validity: '有效期',
    publicKey: '公鑰',
    algorithms: '密碼演算法',
    downloadPEM: '下載 PEM 憑證',
    recommendation: '建議',

    highRisk: '⚠ 高風險',
    mediumRisk: '⚡ 中風險',
    lowRisk: '✓ 低風險',

    adviceHigh: '此網址的金鑰交換 (KEX) 和數位簽章 (PKI) 均不支援 PQC 演算法，面臨量子電腦攻擊的高風險。建議盡快規劃遷移至後量子密碼學標準。',
    adviceMediumKex: '金鑰交換 KEM 已支援 PQC，但數位簽章 PKI 尚未升級。建議同步升級簽章演算法。',
    adviceMediumPki: '數位簽章 PKI 已支援 PQC，但金鑰交換 KEX 尚未升級。建議同步升級金鑰交換機制。',
    adviceLow: '金鑰交換與數位簽章均已支援 PQC 演算法，具備良好的量子安全性。',

    footerLeft: '© 解智能資料梳理股份有限公司 · DataBrushing AI',
    footerRight: 'AI | Security | IP | Consultancy · https://databrushing.ai',

    feature1: 'TLS 1.3 / SSH / STARTTLS PQC 分析',
    feature2: 'ML-KEM · ML-DSA · SLH-DSA 偵測',
    feature3: 'X.509 憑證與 DNSSEC 稽核',
    feature4: 'NIST SP 800-208 / CNSA 2.0 報告',
    platformSubtitle: '企業級量子安全就緒度評估平台',
  },
} as const;

export type Translations = typeof t.en;
